import React, { type CSSProperties } from 'react'
import StyledButton, { ButtonSize, ButtonType, StyledIconButton } from './styles'
import { CircularProgress, type CircularProgressProps, type SxProps } from '@mui/material'
import { blue, grey } from '../../colors'
import { type Theme } from '@mui/material/styles'
export interface GenericButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'color'> {
  id?: string
  ref?: React.Ref<any>
  variant?: 'text' | 'outlined' | 'contained' | undefined
  label?: string | React.ReactNode
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  buttonType?: ButtonType
  disabled?: boolean
  buttonSize?: ButtonSize
  icon?: React.ReactNode
  textTransform?: string
  fontWeight?: string
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset' | undefined
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  style?: CSSProperties
  loading?: boolean
  circularProgressStyles?: CSSProperties
  circularProgressProps?: CircularProgressProps
  title?: string
  sx?: SxProps<Theme>
}
// Generic Button is responsible for rendering IconButton or Button
// Button have few variants like Button can have start or end icon along with label
// And Icon can have only icon without any text
// If label is not passed and icon is passed then Generic Button will return IconButton instead of Button
// https://mui.com/material-ui/react-button/

const GenericButton: React.FC<GenericButtonProps> = ({
  ref,
  label,
  startIcon,
  endIcon,
  onClick,
  disabled = false,
  buttonType,
  buttonSize = ButtonSize.Large,
  icon,
  textTransform = 'none',
  fontWeight = 600,
  fullWidth,
  type,
  style,
  loading = false,
  circularProgressStyles,
  circularProgressProps,
  title,
  sx = {},
  ...props
}) => {
  const getCircularProgressSize = (buttonSize: ButtonSize): number => {
    switch (buttonSize) {
      case 'xxl':
        return 36
      case 'xl':
        return 32
      case 'Large':
        return 28
      case 'Medium':
        return 24
      case 'Small':
        return 20
      default:
        return 20
    }
  }

  const circularProgressColor = buttonType === ButtonType.Primary || buttonType === ButtonType.Destructive ? grey[0] : blue[500]
  const iconButtonIcon = loading ? <CircularProgress thickness={5} size={getCircularProgressSize(buttonSize)} {...circularProgressProps} sx={{ color: circularProgressColor, ...circularProgressStyles }} /> : icon

  return (
    (label != null && !loading)
      ? <StyledButton
        fullWidth={fullWidth}
        type={type}
        sx={{ ...(sx as object), ...(textTransform ? { textTransform } : {}), ...(fontWeight ? { fontWeight } : {}) }}
        style={style}
        buttonSize={buttonSize}
        disableRipple
        buttonType={buttonType}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        color={'success'}
        disabled={disabled}
        title={title}
        {...props}
      >
        {label}
      </StyledButton >

      : <StyledIconButton
        ref={ref}
        title={title}
        style={style}
        sx={sx as any}
        type={type}
        buttonSize={buttonSize}
        disableRipple
        buttonType={buttonType}
        onClick={loading ? undefined : onClick}
        disabled={disabled}
        {...props}
      >
        {iconButtonIcon}
      </StyledIconButton>
  )
}

export default GenericButton
